
import {
  Component, Prop, Watch, Mixins,
} from 'vue-property-decorator';
import { showErrorAlertDialog, getPrice } from '@/features/common/helpers';
import { getCarrierLogoCode, getDisplayCarrierCode } from '@/features/common/helpers/carrier';
import { getShipment } from '@/features/shipment-history/services/shipment-history.services';
import { HistoryItemType } from '@/features/shipment-history/types';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { trackShipmentCompleted } from '@/features/common/helpers/tracked-events';
import SkeletonLoaderReturnOptionsVue from './SkeletonLoaderReturnOptions.vue';
import { createLabelForCarriers, parseCarriersData } from '../../services/shipment.services';
import { getBrowserMetadata } from '../../helpers/createShipment';
import { handle3dsCardLookup, redirectTo3dsChallenge, CardLookupError } from '../../helpers/threeDsHelper';
import {
  AvailableService,
  CarrierLabelResult, ExportReasonType, GetLabelData, GetRatesData,
  ThreeDsCardLookupDto,
} from '../../types';

export type ReturnOptions = {
  carrier?: string;
  service?: string;
  price?: number;
  serviceLevel?: string;
  isLoading: boolean;
  message?: string;
}

type TargetOption = 'oneClickReturnOption' | 'cheapestReturnOption';

@Component({
  components: {
    SkeletonLoaderReturnOptionsVue,
  },
})
export default class CreateReturnOptions extends Mixins(ResponsivenessMixin) {
  @Prop({ required: false, default: false }) show!: boolean;

  private oneClickReturnOption: ReturnOptions = {
    isLoading: false,
  };

  private cheapestReturnOption: ReturnOptions = {
    isLoading: false,
  };

  private isLoadingRates = true;

  private showOops = false;

  private currentShipment: HistoryItemType | null = null;

  private labelResponse: CarrierLabelResult | undefined = undefined;

  private noReturnOptionsMessage = '';

  private getPrice = getPrice;

  private getCarrierLogo = (option: ReturnOptions): string => getCarrierLogoCode(option.carrier || '', option.serviceLevel || '');

  private getCarrierDisplay = (option: ReturnOptions): string => getDisplayCarrierCode(option.carrier || '', option.serviceLevel || '');

  get secureshipTrackingNo(): string | null {
    return this.$route.query.id as string;
  }

  get isSubmitting() {
    return this.oneClickReturnOption.isLoading || this.cheapestReturnOption.isLoading;
  }

  get isSameCheapestAndOneClickReturn(): boolean {
    return this.oneClickReturnOption.carrier === this.cheapestReturnOption.carrier
      && this.oneClickReturnOption.serviceLevel === this.cheapestReturnOption.serviceLevel;
  }

  @Watch('secureshipTrackingNo', { immediate: true })
  private async loadCurrentShipmentAndServices(trackingNumber?: string) {
    if (!trackingNumber) return;

    this.isLoadingRates = true;

    this.currentShipment = await getShipment(trackingNumber);

    await this.fetchServicesAndSetReturnOptions();

    this.isLoadingRates = false;
  }

  private async performCreateReturn(target: TargetOption) {
    if (!this.secureshipTrackingNo) {
      showErrorAlertDialog(this.$t('shipment.alertDialog.trackingNumberInvalid'));
      return;
    }

    this[target].isLoading = true;
    await this.createReturnShipment(this[target].serviceLevel);

    if (this.labelResponse?.errorType === 'CreditCardChallengeRequired' && this.labelResponse?.threeDsChallenge) {
      return;
    }

    this[target].isLoading = false;
  }

  private async fetchServicesAndSetReturnOptions() {
    if (!this.currentShipment) return;

    const {
      toAddress,
      fromAddress,
      packages,
      shipDateTime,
      isDocumentsOnly,
      selectedService,
      carrierCode,
      isReturnShipment,
      isBoxFree,
      isLabelFree,
    } = this.currentShipment;

    const data: GetRatesData = {
      currencyCode: 'CAD',
      fromAddress: toAddress, // address swapped for return
      toAddress: fromAddress, // address swapped for return
      packages,
      shipDateTime,
      documentsOnly: isDocumentsOnly || false,
      returnShipment: isReturnShipment || false,
      boxFree: isBoxFree || false,
      labelFree: isLabelFree || false,
    };

    const ratesResponse = await parseCarriersData(data, 'CreateShipment');

    if (!ratesResponse || ratesResponse.rates.length === 0) {
      this.noReturnOptionsMessage = this.$t('shipment.returnPage.returnInfo.errorMessage.emptyReturns') as string;
      return;
    }

    const oneClickReturnService = ratesResponse.rates.find(
      (rate) => rate.selectedService === selectedService,
    );

    if (!oneClickReturnService) {
      this.oneClickReturnOption.message = this.$t(
        'shipment.returnPage.returnInfo.errorMessage.noReturnService', {
          carrier: carrierCode,
        },
      ) as string;
    }

    this.setReturnOption('oneClickReturnOption', oneClickReturnService);

    const cheapestReturnService = ratesResponse.rates.reduce(
      (acc, rate) => (rate.total < acc.total ? rate : acc),
    );

    this.setReturnOption('cheapestReturnOption', cheapestReturnService);
  }

  private setReturnOption(target: TargetOption, service: AvailableService | undefined) {
    if (!service) return;

    const {
      carrierCode, serviceName, total, selectedService: serviceLevel,
    } = service;

    this[target] = {
      carrier: carrierCode,
      service: serviceName,
      price: total,
      serviceLevel,
      isLoading: false,
    };
  }

  private async createReturnShipment(serviceLevel?: string) {
    if (!this.secureshipTrackingNo || !serviceLevel) return;

    if (!this.currentShipment) {
      showErrorAlertDialog(this.$t('shipment.alertDialog.trackingNumberInvalid'));
      return;
    }

    const {
      toAddress,
      fromAddress,
      packages,
      deliveryEmail,
      billing,
      isDocumentsOnly,
      ecommerce,
      reference,
      comment,
      shipDateTime,
      isReturnShipment,
      isBoxFree,
      isLabelFree,
    } = this.currentShipment;

    let threeDsData: ThreeDsCardLookupDto | null = null;
    try {
      threeDsData = await handle3dsCardLookup();
    } catch (error) {
      if (error instanceof CardLookupError) {
        this.labelResponse = {
          successful: false,
          errorMessage: error.errorMessage,
          redirectUrl: '',
          isCarrierMessage: false,
        } as CarrierLabelResult;
        this.showOops = true;
        return;
      }
      // If it's not a CardLookupError, rethrow it
      throw error;
    }

    const metaData = getBrowserMetadata(threeDsData?.cardLookupId);

    const data: GetLabelData = {
      selectedService: serviceLevel,
      fromAddress: toAddress, // address swapped for return
      toAddress: fromAddress, // address swapped for return
      packages,
      shipDateTime,
      deliveryEmails: deliveryEmail ? [deliveryEmail] : [],
      billingOption: billing.billingType,
      documentsOnly: isDocumentsOnly,
      billingAccountNumber: billing.account,
      ecommerce,
      references: reference ? [reference] : [],
      comments: comment || '',
      commercialInvoice: undefined,
      returnShipment: isReturnShipment || false,
      boxFree: isBoxFree || false,
      labelFree: isLabelFree || false,
      metaData,
    };

    const uploadDocs = this.currentShipment.uploadDocuments || [];
    const cleanDocIds = uploadDocs
      .filter((doc) => doc.status === 'Clean')
      .map((doc) => doc.documentId);

    if (cleanDocIds.length) {
      data.documentIds = cleanDocIds;
    }

    if (this.currentShipment.commercialInvoice) {
      const {
        reasonForExport,
        generalDescriptionOfGoods,
        currencyCode, remarks, declarationStatement, customsParts,
        taxId,
      } = this.currentShipment.commercialInvoice;

      data.commercialInvoice = {
        reasonForExport: (reasonForExport || '') as keyof typeof ExportReasonType | '',
        generalDescriptionOfGoods,
        currencyCode,
        remarks,
        declarationStatement,
        taxId,
        customsParts: customsParts || [],
      };
    }

    this.labelResponse = await createLabelForCarriers(data);

    if (this.labelResponse && this.labelResponse.successful) {
      this.$store.dispatch('refetchUserInfo');
      const selectedSvc = this.$store.getters.getSelectedService;
      trackShipmentCompleted(this.$gtag, {
        shipmentId: this.labelResponse.secureshipTrackingNo,
        value: selectedSvc?.total || 0,
      });
      this.$router.push({
        name: 'shipment-return',
        query: {
          id: this.labelResponse.secureshipTrackingNo,
        },
      });
      return;
    }

    if (this.labelResponse?.errorType === 'CreditCardChallengeRequired' && this.labelResponse?.threeDsChallenge) {
      await redirectTo3dsChallenge(this.labelResponse.threeDsChallenge);
      return;
    }

    this.showOops = true;
  }
}
